import { atom } from "jotai";

/**
 * localStorage key holding the impersonation CONTEXT (Sprint 139, TASK-3906).
 * Persisted alongside the `superadmin_*` token backup so the exit flow can
 * compute the correct destination and the banner can render the right label
 * across a page refresh.
 */
const IMPERSONATION_CONTEXT_KEY = "impersonation_context";

/**
 * Who initiated the current impersonation. Determines the exit destination
 * (Decision D5): a superadmin actor returns to `/admin/dashboard`; a dealer
 * actor returns to `/dealer/dashboard`.
 */
export type ImpersonationActorKind = "superadmin" | "dealer";

/** What the actor is impersonating: a tenant (dispatcher shell) or a dealer. */
export type ImpersonationTargetKind = "tenant" | "dealer";

/**
 * Persisted metadata about the active impersonation session (Sprint 139).
 *
 * Recorded at start so the shared exit hook knows the destination without
 * re-deriving it from the (now-impersonated) identity, and so the banner can
 * label the session (a dealer target has no `tenant_name` on the user profile).
 */
export interface ImpersonationContext {
  /** Who started the impersonation — drives the exit destination (D5). */
  actorKind: ImpersonationActorKind;
  /** What is being impersonated. */
  targetKind: ImpersonationTargetKind;
  /** Path to navigate to on exit (e.g. `/admin/dashboard`, `/dealer/dashboard`). */
  exitPath: string;
  /**
   * Human-readable target name for the banner (tenant name or dealer name).
   * Optional — the tenant flow can also read `user.tenant_name`.
   */
  displayName?: string;
  /**
   * The unique id of the impersonation target — `tenant_id` for a tenant
   * target, the dealer user id for a dealer target. Used to key brand/identity
   * re-resolution so two same-named targets (or an empty `displayName`) don't
   * collide on the same key. Optional for backward compatibility with contexts
   * persisted before this field existed.
   */
  targetId?: number;
}

/** Read the persisted impersonation context, or `null` when none is active. */
export function readImpersonationContext(): ImpersonationContext | null {
  const raw = localStorage.getItem(IMPERSONATION_CONTEXT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ImpersonationContext;
  } catch {
    // Corrupt/legacy value — treat as absent rather than crashing the banner.
    return null;
  }
}

/** Persist the impersonation context at session start. */
export function writeImpersonationContext(ctx: ImpersonationContext): void {
  localStorage.setItem(IMPERSONATION_CONTEXT_KEY, JSON.stringify(ctx));
}

/** Clear the persisted impersonation context at session end. */
export function clearImpersonationContext(): void {
  localStorage.removeItem(IMPERSONATION_CONTEXT_KEY);
}

/**
 * Tracks whether an impersonation session is currently active (a superadmin
 * impersonating a tenant/dealer, or a dealer impersonating a tenant).
 * Initialized from localStorage so the banner persists across page refreshes.
 */
export const isImpersonatingAtom = atom<boolean>(
  !!localStorage.getItem("superadmin_access_token"),
);

/**
 * The active impersonation context, mirrored into Jotai so components re-render
 * when it changes. Initialized from localStorage; keep it in sync by setting it
 * alongside {@link writeImpersonationContext} / {@link clearImpersonationContext}
 * (the start/exit flows do exactly this).
 */
export const impersonationContextAtom = atom<ImpersonationContext | null>(
  readImpersonationContext(),
);
